import { apiClient } from '@/lib/api/client';
import {
  DashboardMetricsSchema,
  DashboardApiResponseSchema,
  GeographicDataListSchema,
  TimeSeriesDataSchema,
  PieChartDataSchema,
  DashboardQueryParamsSchema,
  PendingStepsDataSchema,
  DeviceDataSchema,
  BrowserDataSchema,
  DurationPercentilesDataSchema,
  type DashboardMetrics,
  type DashboardApiResponse,
  type GeographicDataList,
  type TimeSeriesData,
  type PieChartData,
  type DashboardQueryParams,
  type PendingStepsData,
  type DeviceData,
  type BrowserData,
  type DurationPercentilesData,
} from './dashboard.schemas';

// Re-export types for use in hooks and components
export type {
  DashboardMetrics,
  DashboardApiResponse,
  GeographicDataList,
  TimeSeriesData,
  PieChartData,
  DashboardQueryParams,
  PendingStepsData,
  DeviceData,
  BrowserData,
  DurationPercentilesData,
};

// ============================================================================
// Dashboard Metrics API Functions
// ============================================================================

/**
 * Fetch dashboard metrics data
 * @param params - Optional query parameters for filtering metrics
 * @returns Promise<DashboardMetrics> - The dashboard metrics
 */
export async function fetchDashboardMetrics(
  params?: DashboardQueryParams
): Promise<DashboardMetrics> {
  // Get user session and selected organization/project
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization, selectedProject } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  if (!selectedProject) {
    throw new Error('No project selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  // Note: We don't validate project existence in session metadata since 
  // projects are now fetched from API, not from session

  const organizationId = selectedOrganization.id;
  const projectId = selectedProject.id;

  // Validate input parameters with Zod
  const validatedParams = params ? DashboardQueryParamsSchema.parse(params) : {
    startDate: undefined,
    endDate: undefined,
    period: 'month' as const,
    timezone: undefined,
  };

  // Convert dates to timestamps for API
  const apiParams: Record<string, any> = {};
  if (validatedParams.startDate) {
    apiParams.start = new Date(validatedParams.startDate).getTime();
  }
  if (validatedParams.endDate) {
    apiParams.end = new Date(validatedParams.endDate).getTime();
  }
  if (validatedParams.period) {
    apiParams.period = validatedParams.period;
  }
  if (validatedParams.timezone) {
    apiParams.timezone = validatedParams.timezone;
  }

  // Make API request to correct endpoint
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}/verifications/metrics`, {
    params: apiParams,
  });

  // Transform the raw API response to match our DashboardMetrics schema
  // This handles the conversion from the actual server response format
  const rawData = DashboardApiResponseSchema.parse(response.data);

  // Calculate metrics from the raw data
  const totalValidations = rawData.statusCounter.total || 0;
  const successfulValidations = rawData.statusCounter.finished.approved || 0;
  const failedValidations = rawData.statusCounter.finished.rejected || 0;
  const pendingValidations = Object.values(rawData.statusCounter.pendings).reduce((sum, count) => sum + count, 0);

  const validationRate = totalValidations > 0
    ? (successfulValidations / totalValidations) * 100
    : 0;

  const uniqueDevices = rawData.statusCounter.devices?.unique ?? 0;
  const approvedDevices = rawData.statusCounter.devices?.approved ?? 0;
  const deviceConversionRate = rawData.statusCounter.devices?.conversionRate ?? 0;

  // Create period based on params or default to current month
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const metrics: DashboardMetrics = {
    total_validations: totalValidations,
    successful_validations: successfulValidations,
    failed_validations: failedValidations,
    pending_validations: pendingValidations,
    validation_rate: Math.round(validationRate * 100) / 100, // Round to 2 decimal places
    unique_devices: uniqueDevices,
    approved_devices: approvedDevices,
    device_conversion_rate: deviceConversionRate,
    period: {
      start: validatedParams.startDate || startOfMonth.toISOString(),
      end: validatedParams.endDate || endOfMonth.toISOString(),
    },
  };

  // Validate response with Zod before returning
  return DashboardMetricsSchema.parse(metrics);
}

/**
 * Fetch raw dashboard data (original API format)
 * @param params - Optional query parameters
 * @returns Promise<DashboardApiResponse> - The raw dashboard API response
 */
export async function fetchDashboardData(
  params?: DashboardQueryParams
): Promise<DashboardApiResponse> {
  // Get user session and selected organization/project
  const { useAuthStore } = await import('@/store/authStore');
  const { useSettingsStore } = await import('@/store/settingsStore');
  const session = useAuthStore.getState().session;
  const { selectedOrganization, selectedProject } = useSettingsStore.getState();
  
  if (!session?.identity?.metadata_public?.tenants) {
    throw new Error('No tenant information available in session');
  }

  if (!selectedOrganization) {
    throw new Error('No organization selected');
  }

  if (!selectedProject) {
    throw new Error('No project selected');
  }

  // Verify the selected organization exists in session
  const tenant = session.identity.metadata_public.tenants.find(
    t => t.id === selectedOrganization.id
  );
  
  if (!tenant) {
    throw new Error('Selected organization not found in session');
  }

  // Note: We don't validate project existence in session metadata since 
  // projects are now fetched from API, not from session

  const organizationId = selectedOrganization.id;
  const projectId = selectedProject.id;

  // Validate input parameters with Zod
  const validatedParams = params ? DashboardQueryParamsSchema.parse(params) : {
    startDate: undefined,
    endDate: undefined,
    period: 'month' as const,
    timezone: undefined,
  };

  // Convert dates to timestamps for API
  const apiParams: Record<string, any> = {};
  if (validatedParams.startDate) {
    apiParams.start = new Date(validatedParams.startDate).getTime();
  }
  if (validatedParams.endDate) {
    apiParams.end = new Date(validatedParams.endDate).getTime();
  }
  if (validatedParams.period) {
    apiParams.period = validatedParams.period;
  }
  if (validatedParams.timezone) {
    apiParams.timezone = validatedParams.timezone;
  }

  // Make API request to the correct endpoint
  const response = await apiClient.get(`/organizations/${organizationId}/projects/${projectId}/verifications/metrics`, {
    params: apiParams,
  });

  // Validate response with Zod
  return DashboardApiResponseSchema.parse(response.data);
}

// ============================================================================
// Chart Data API Functions
// ============================================================================

/**
 * Fetch pie chart data for general status
 * @param params - Optional query parameters
 * @returns Promise<PieChartData> - Pie chart data for general status
 */
export async function fetchGeneralStatusChart(
  params?: DashboardQueryParams
): Promise<PieChartData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform general status data to pie chart format with improved labels
  const chartData = Object.entries(dashboardData.statusCounter.general).map(([key, value]) => {
    let displayName = key.charAt(0).toUpperCase() + key.slice(1);
    
    // Improve label terminology for better user understanding
    if (key.toLowerCase() === 'failed' || key.toLowerCase() === 'rejected') {
      displayName = 'Rejected';
    } else if (key.toLowerCase() === 'success' || key.toLowerCase() === 'successful' || key.toLowerCase() === 'approved') {
      displayName = 'Approved';
    }
    
    return {
      name: displayName,
      value,
    };
  });

  const pieChartData: PieChartData = {
    data: chartData,
    title: 'General Status',
    legend: chartData.map(item => item.name),
    type: 'pie',
    radius: ['40%', '70%'],
  };

  // Validate response with Zod
  return PieChartDataSchema.parse(pieChartData);
}

/**
 * Fetch pie chart data for pending validations
 * @param params - Optional query parameters
 * @returns Promise<PieChartData> - Pie chart data for pending validations
 */
export async function fetchPendingValidationsChart(
  params?: DashboardQueryParams
): Promise<PieChartData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform pending status data to pie chart format
  const chartData = Object.entries(dashboardData.statusCounter.pendings).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
  }));

  const pieChartData: PieChartData = {
    data: chartData,
    title: 'Pending Validations',
    legend: chartData.map(item => item.name),
    type: 'pie',
    radius: ['40%', '70%'],
  };

  // Validate response with Zod
  return PieChartDataSchema.parse(pieChartData);
}

/**
 * Fetch pie chart data for finished validations
 * @param params - Optional query parameters
 * @returns Promise<PieChartData> - Pie chart data for finished validations
 */
export async function fetchFinishedValidationsChart(
  params?: DashboardQueryParams
): Promise<PieChartData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform finished status data to pie chart format with improved labels
  const chartData = Object.entries(dashboardData.statusCounter.finished).map(([key, value]) => {
    let displayName = key.charAt(0).toUpperCase() + key.slice(1);
    
    // Improve label terminology for better user understanding
    if (key.toLowerCase() === 'failed' || key.toLowerCase() === 'rejected') {
      displayName = 'Rejected';
    } else if (key.toLowerCase() === 'success' || key.toLowerCase() === 'successful' || key.toLowerCase() === 'approved') {
      displayName = 'Approved';
    }
    
    return {
      name: displayName,
      value,
    };
  });

  const pieChartData: PieChartData = {
    data: chartData,
    title: 'Finished Validations',
    legend: chartData.map(item => item.name),
    type: 'pie',
    radius: ['40%', '70%'],
  };

  // Validate response with Zod
  return PieChartDataSchema.parse(pieChartData);
}

/**
 * Fetch pie chart data for Android SDK versions
 * @param params - Optional query parameters
 * @returns Promise<PieChartData> - Pie chart data for Android SDK versions
 */
export async function fetchAndroidSdkChart(
  params?: DashboardQueryParams
): Promise<PieChartData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform Android SDK data to pie chart format
  // Since androidSdkCounter is currently an empty array, provide default data
  const chartData = Array.isArray(dashboardData.androidSdkCounter) && dashboardData.androidSdkCounter.length > 0
    ? dashboardData.androidSdkCounter.map((item: any) => ({
        name: item.version || 'Unknown',
        value: item.count || 0,
      }))
    : [{ name: 'No Data', value: 0 }];

  const pieChartData: PieChartData = {
    data: chartData,
    title: 'Android SDK Versions',
    legend: chartData.map(item => item.name),
    type: 'pie',
    radius: ['40%', '70%'],
  };

  // Validate response with Zod
  return PieChartDataSchema.parse(pieChartData);
}

/**
 * Fetch time series data for validations by date
 * @param params - Optional query parameters
 * @returns Promise<TimeSeriesData> - Time series data for validations by date
 */
export async function fetchValidationsByDateChart(
  params?: DashboardQueryParams
): Promise<TimeSeriesData> {
  const dashboardData = await fetchDashboardData(params);

  // If validationsByDate is not available, return empty data
  if (!dashboardData.validationsByDate || dashboardData.validationsByDate.date.length === 0) {
    const emptyTimeSeriesData: TimeSeriesData = {
      labels: [],
      data: [],
      title: 'Validations by Date',
      timeseries: true,
    };
    return TimeSeriesDataSchema.parse(emptyTimeSeriesData);
  }

  // Transform validations by date to time series format
  const labels = dashboardData.validationsByDate.date;
  // Convert null values to 0 for better chart display
  const data = dashboardData.validationsByDate.totalData.map(value => value ?? 0);

  const timeSeriesData: TimeSeriesData = {
    labels,
    data,
    title: 'Validations by Date',
    timeseries: true,
  };

  // Validate response with Zod
  return TimeSeriesDataSchema.parse(timeSeriesData);
}

// ============================================================================
// Geographic Data API Functions
// ============================================================================

/**
 * Fetch geographic data for map visualization
 * @param params - Optional query parameters
 * @returns Promise<GeographicDataList> - Geographic data for map
 */
export async function fetchGeographicData(
  params?: DashboardQueryParams
): Promise<GeographicDataList> {
  try {
    // Use the existing fetchDashboardData function to get metrics data
    // which includes geolocations field
    const dashboardData = await fetchDashboardData(params);

    // Extract geolocations from the metrics response
    // The geolocations field comes from the /verifications/metrics endpoint
    const geolocations = dashboardData.geolocations || [];

    // Transform and validate each geolocation item
    const transformedGeolocations = geolocations
      .map((item: any, index: number) => {
        try {
          // Handle different possible data structures from the server
          if (!item || typeof item !== 'object') {
            return null;
          }

          // Try to extract coordinates from various possible formats
          let coordinates = { lat: 0, lng: 0 };
          if (item.coordinates) {
            coordinates = {
              lat: Number(item.coordinates.lat) || 0,
              lng: Number(item.coordinates.lng) || 0,
            };
          } else if (item.lat !== undefined && item.lng !== undefined) {
            coordinates = {
              lat: Number(item.lat) || 0,
              lng: Number(item.lng) || 0,
            };
          } else if (item.latitude !== undefined && item.longitude !== undefined) {
            coordinates = {
              lat: Number(item.latitude) || 0,
              lng: Number(item.longitude) || 0,
            };
          }

          // Validate coordinates are within valid ranges
          if (coordinates.lat < -90 || coordinates.lat > 90 || 
              coordinates.lng < -180 || coordinates.lng > 180) {
            return null;
          }

          return {
            id: item.id || `geo-${index}`,
            coordinates,
            country: item.country || item.country_name || null,
            country_code: item.country_code || item.countryCode || null,
            validations: Number(item.validations) || Number(item.count) || 0,
          };
        } catch (error) {
          console.warn('Failed to transform geolocation item:', item, error);
          return null;
        }
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    // Validate response with Zod
    return GeographicDataListSchema.parse(transformedGeolocations);
  } catch (error) {
    console.warn('Failed to fetch geographic data:', error);
    // Return empty array if there's any error
    return GeographicDataListSchema.parse([]);
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get default dashboard query parameters for current month
 * @returns DashboardQueryParams - Default parameters
 */
export function getDefaultDashboardParams(): DashboardQueryParams {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    startDate: startOfMonth.toISOString(),
    endDate: endOfMonth.toISOString(),
    period: 'month',
  };
}

/**
 * Validate dashboard query parameters
 * @param params - Parameters to validate
 * @returns DashboardQueryParams - Validated parameters
 */
export function validateDashboardParams(params: unknown): DashboardQueryParams {
  return DashboardQueryParamsSchema.parse(params);
}

// ============================================================================
// New Chart Data API Functions
// ============================================================================

/**
 * Fetch pending verification steps breakdown data
 * @param params - Optional query parameters
 * @returns Promise<PendingStepsData> - Pending steps breakdown data
 */
export async function fetchPendingStepsChart(
  params?: DashboardQueryParams
): Promise<PendingStepsData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform pending steps data to chart format
  const chartData = Object.entries(dashboardData.statusCounter.pendings).map(([key, value]) => ({
    name: key.charAt(0).toUpperCase() + key.slice(1),
    value,
  }));

  const pendingStepsData: PendingStepsData = {
    data: chartData,
    title: 'Verification Steps Breakdown',
    type: 'bar',
  };

  // Validate response with Zod
  return PendingStepsDataSchema.parse(pendingStepsData);
}

/**
 * Fetch device usage data
 * @param params - Optional query parameters
 * @returns Promise<DeviceData> - Device usage data
 */
export async function fetchDeviceChart(
  params?: DashboardQueryParams
): Promise<DeviceData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform device data to chart format (top 6 devices)
  const sortedDevices = dashboardData.devicesCounter
    .sort((a: any, b: any) => b.count - a.count)
    .slice(0, 6);

  const chartData = sortedDevices.map((device: any) => ({
    name: device.deviceName.length > 20 
      ? device.deviceName.substring(0, 20) + '...' 
      : device.deviceName,
    value: device.count,
  }));

  const deviceData: DeviceData = {
    data: chartData,
    title: 'Top Devices Used',
    type: 'pie',
  };

  // Validate response with Zod
  return DeviceDataSchema.parse(deviceData);
}

/**
 * Fetch browser usage data
 * @param params - Optional query parameters
 * @returns Promise<BrowserData> - Browser usage data
 */
export async function fetchBrowserChart(
  params?: DashboardQueryParams
): Promise<BrowserData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform browser data to chart format
  const chartData = dashboardData.browsersCounter.map((browser: any) => ({
    name: browser.browserName,
    value: browser.count,
  }));

  const browserData: BrowserData = {
    data: chartData,
    title: 'Browsers Used',
    type: 'pie',
  };

  // Validate response with Zod
  return BrowserDataSchema.parse(browserData);
}

/**
 * Fetch validation duration percentiles data
 * @param params - Optional query parameters
 * @returns Promise<DurationPercentilesData> - Duration percentiles data
 */
export async function fetchDurationPercentilesChart(
  params?: DashboardQueryParams
): Promise<DurationPercentilesData> {
  const dashboardData = await fetchDashboardData(params);

  // Transform duration data to chart format
  const chartData = dashboardData.validationDuration.map(item => ({
    name: item.name.toUpperCase(),
    value: parseFloat(item.value),
  }));

  const durationData: DurationPercentilesData = {
    data: chartData,
    title: 'Validation Duration Percentiles',
    type: 'bar',
    unit: 'seconds',
  };

  // Validate response with Zod
  return DurationPercentilesDataSchema.parse(durationData);
}